import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useMemo, useState } from "react";
import { Button, ButtonProps, OverlayTrigger } from "react-bootstrap";
import { defineMessage, defineMessages, FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import schema from "../../../src/gen/schema/witbooking.backoffice.inventory.v1/InventoryState.json";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { DayOfWeek } from "../../gen/witbooking/backoffice/common/common_pb";
import { InventoryAPIPromiseClient } from "../../gen/witbooking/backoffice/inventory/v1/inventory_api_grpc_web_pb";
import {
  DeleteInventoryLineRequest,
  GetInventoryLinesRequest,
  InventoryState,
  RateValue,
  SharedOwnUnionValue,
  UpdateSingleInventoryLineRequest,
} from "../../gen/witbooking/backoffice/inventory/v1/inventory_api_pb";
import { dateToString } from "../../lib/dateUtils";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { useApiClient } from "../../lib/http";
import { regenPropertyCache } from "../common/api";
import { inventoryQueryKeys } from "../inventory/inventory.queryKeys";
import { useInventoryService } from "../inventory/inventory.service";
import { DefaultTableRowActionsProps, Delete } from "../shared/actions/tableActions";
import { inputFilter } from "../shared/crud/filter-widgets";
import { FormConfiguration } from "../shared/crud/form";
import { TableColumns, TableFilters } from "../shared/crud/list";
import SimpleCrud, { CrudMessages, defaultCrudMessages } from "../shared/crud/simple-crud";
import { RendererProps } from "../shared/renderers";
import * as TableRenderers from "../shared/renderers/table";
import { mapRenderer, StringRenderer } from "../shared/renderers/table";
import { ArrayAsString } from "../shared/widgets/arrayAsString";
import { DateRangePicker } from "../shared/widgets/dateRangePicker";
import { InventoryStateSelector } from "../shared/widgets/inventoryStateSelector";
import SectionUnderline from "../shared/widgets/sectionUnderline";
import { FormContainer, StyledTooltip } from "./form-style";

const msgs = defineMessages({
  visibility: {
    id: "inventoryTable.visibility",
    defaultMessage: "visibility",
  },
  edit: {
    id: "inventoryTable.edit",
    defaultMessage: "edit",
  },
  delete: {
    id: "inventoryTable.delete",
    defaultMessage: "delete",
  },
  onExportText: defineMessage({
    id: "inventoryTable.onExportText",
    defaultMessage: "export",
  }),
  own: {
    id: "inventoryTable.own",
    description: "own",
    defaultMessage: "own",
  },
  0: {
    id: "inventoryTable.monday",
    description: "monday",
    defaultMessage: "monday",
  },
  1: {
    id: "inventoryTable.tuesday",
    description: "tuesday",
    defaultMessage: "tuesday",
  },
  2: {
    id: "inventoryTable.wednesday",
    description: "wednesday",
    defaultMessage: "wednesday",
  },
  3: {
    id: "inventoryTable.thursday",
    description: "thursday",
    defaultMessage: "thursday",
  },
  4: {
    id: "inventoryTable.friday",
    description: "friday",
    defaultMessage: "friday",
  },
  5: {
    id: "inventoryTable.saturday",
    description: "saturday",
    defaultMessage: "saturday",
  },
  6: {
    id: "inventoryTable.sunday",
    description: "sunday",
    defaultMessage: "sunday",
  },
});

const messages: CrudMessages = {
  ...defaultCrudMessages,
  tableHeaders: {
    ticker: defineMessage({
      id: "inventoryTable.ticker",
      defaultMessage: "ticker",
    }),
    accommodation: defineMessage({
      id: "inventoryTable.accommodation",
      defaultMessage: "accommodation",
    }),
    configuration: defineMessage({
      id: "inventoryTable.configuration",
      defaultMessage: "configuration",
    }),
    mealPlan: defineMessage({
      id: "inventoryTable.meal_plan",
      defaultMessage: "meal_plan",
    }),
    condition: defineMessage({
      id: "inventoryTable.condition",
      defaultMessage: "condition",
    }),
    rate: defineMessage({
      id: "inventoryTable.rate",
      defaultMessage: "rate",
    }),
    availability: defineMessage({
      id: "inventoryTable.availability",
      defaultMessage: "availability",
    }),
    lock: defineMessage({
      id: "inventoryTable.lock",
      defaultMessage: "lock",
    }),
    minStay: defineMessage({
      id: "inventoryTable.min_stay",
      defaultMessage: "min_stay",
    }),
    maxStay: defineMessage({
      id: "inventoryTable.max_stay",
      defaultMessage: "max_stay",
    }),
    minNotice: defineMessage({
      id: "inventoryTable.min_notice",
      defaultMessage: "min_notice",
    }),
    maxNotice: defineMessage({
      id: "inventoryTable.max_notice",
      defaultMessage: "max_notice",
    }),
    codesList: defineMessage({
      id: "inventoryTable.codes",
      defaultMessage: "codes",
    }),
    checkinDaysList: defineMessage({
      id: "inventoryTable.checkin_days",
      defaultMessage: "checkin_days",
    }),
    checkoutDaysList: defineMessage({
      id: "inventoryTable.checkout_days",
      defaultMessage: "checkout_days",
    }),
    rackRate: defineMessage({
      id: "inventoryTable.rack_rate",
      defaultMessage: "rack_rate",
    }),
    bookingWindowPeriodTo: defineMessage({
      id: "inventoryTable.booking_window_period_to",
      defaultMessage: "Hasta",
    }),
    bookingWindowPeriodFrom: defineMessage({
      id: "inventoryTable.booking_window_period_from",
      defaultMessage: "Desde",
    }),
    closeToArrivalOwn: defineMessage({
      id: "inventoryTable.closed_arrival",
      defaultMessage: "CTA",
    }),
    closeToDepartureOwn: defineMessage({
      id: "inventoryTable.closed_departure",
      defaultMessage: "CTD",
    }),
  },
  entityName: defineMessage({ id: "inventoryTable.entityName", defaultMessage: "Inventory Table" }),
};

const CTACTD = {
  $schema: "http://json-schema.org/draft-07/schema#",
  properties: {
    parentTicker: {
      type: "string",
    },
    own: {
      type: "string",
    },
    days: {
      items: {
        enum: [
          "MONDAY",
          0,
          "TUESDAY",
          1,
          "WEDNESDAY",
          2,
          "THURSDAY",
          3,
          "FRIDAY",
          4,
          "SATURDAY",
          5,
          "SUNDAY",
          6,
        ],
      },
      type: "array",
    },
  },
  additionalProperties: false,
  type: "object",
  id: "witbooking.backoffice.inventory.v1.SharedOwnUnionValue",
};

let modifiedSchema: any = schema;
//Schema fixes
//Fixing badly generated refs

// @ts-ignore
modifiedSchema.properties.codesList = modifiedSchema.properties.codes;
delete modifiedSchema.properties.codes;
// @ts-ignore
modifiedSchema.properties.checkinDaysList = modifiedSchema.properties.checkinDays;
delete modifiedSchema.properties.checkinDays;
// @ts-ignore
modifiedSchema.properties.checkoutDaysList = modifiedSchema.properties.checkoutDays;
delete modifiedSchema.properties.checkoutDays;

modifiedSchema.properties.availability.$ref =
  "#/definitions/witbooking.backoffice.inventory.v1.SharedOwnUnionValue";
modifiedSchema.properties.lock.$ref =
  "#/definitions/witbooking.backoffice.inventory.v1.SharedOwnUnionValue";
modifiedSchema.properties.minStay.$ref =
  "#/definitions/witbooking.backoffice.inventory.v1.ConstantSharedOwnUnionValue";
modifiedSchema.properties.maxStay.$ref =
  "#/definitions/witbooking.backoffice.inventory.v1.ConstantSharedOwnUnionValue";
modifiedSchema.properties.minNotice.$ref =
  "#/definitions/witbooking.backoffice.inventory.v1.ConstantSharedOwnUnionValue";
modifiedSchema.properties.maxNotice.$ref =
  "#/definitions/witbooking.backoffice.inventory.v1.ConstantSharedOwnUnionValue";

// @ts-ignore
modifiedSchema.properties.closeToArrival = CTACTD;
// @ts-ignore
modifiedSchema.properties.closeToDeparture = CTACTD;
// @ts-ignore
modifiedSchema.properties.inventorySection = {
  type: "string",
};
// @ts-ignore
modifiedSchema.properties.validitySection = {
  type: "string",
};
// @ts-ignore
modifiedSchema.properties.restrictionsSection = {
  type: "string",
};

const fromTo = {
  anyOf: [
    {
      type: "string",
      format: "date-time",
    },
    {
      type: "string",
      maxLength: 0,
    },
  ],
};

// @ts-ignore
modifiedSchema.properties.bookingWindowPeriod.properties.from = fromTo;
// @ts-ignore
modifiedSchema.properties.bookingWindowPeriod.properties.to = fromTo;

const defaultPageSize = 50;
const pageSizes = [50, 100, 500];

const tooltipConfig = {
  delay: { show: 250, hide: 0 },
};

interface ButtonConfig extends ButtonProps {
  className: string;
}

interface ExtendedInventoryState extends InventoryState.AsObject {
  closeToArrival?: SharedOwnUnionValue.AsObject & { days: DayOfWeek[] };
  closeToDeparture?: SharedOwnUnionValue.AsObject & { days: DayOfWeek[] };
  inventorySection?: string;
  validitySection?: string;
  restrictionsSection?: string;
  originalInventoryState: InventoryState;
  bookingWindowPeriodTo: string;
  bookingWindowPeriodFrom: string;
  closeToArrivalOwn: string;
  closeToDepartureOwn: string;
}

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.regularTextColor};
`;

const buttonConfig: ButtonConfig = {
  disabled: false,
  size: "sm",
  className: "mr-2",
  variant: "light",
};

// @ts-ignore
const visibilityTooltip = props => (
  <StyledTooltip id="cancel-tooltip" {...props}>
    <FormattedMessage {...msgs.visibility} />
  </StyledTooltip>
);

// @ts-ignore
const deleteTooltip = props => (
  <StyledTooltip id="delete-tooltip" {...props}>
    <FormattedMessage {...msgs.delete} />
  </StyledTooltip>
);

// @ts-ignore
const fullInventoryTickerTooltip = (props, rows?: any) => (
  <StyledTooltip key={rows.id} {...props}>
    {rows.tickerName}
  </StyledTooltip>
);

const syncFilter = (data: string, filter: string) => {
  return data.toLowerCase().includes(filter.toLowerCase());
};

function daysInverter(days: DayOfWeek[]): number[] {
  const invertedDays: number[] = [];
  if (Array.isArray(days)) {
    for (let i = 0; i < 7; i++) {
      if (!days.includes(i)) {
        invertedDays.push(i);
      }
    }
  }
  return invertedDays;
}

function InventoryTable({ propertyTicker, section }: { propertyTicker: string; section: string }) {
  const [forceUpdate, setForceUpdate] = useState(0);
  const client = useGrpcClient(InventoryAPIPromiseClient);
  const getInventoryLines = usePropertyGrpcCall(client, client.getInventoryLines);
  const updateSingleInventoryLine = usePropertyGrpcCall(client, client.updateSingleInventoryLine);
  const deleteInventoryLine = usePropertyGrpcCall(client, client.deleteInventoryLine);
  const httpClientCache = useApiClient();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const inventoryService = useInventoryService();

  const invalidateInventoryTableRows = useCallback(() => {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.table(propertyTicker) }),
      queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.state(propertyTicker) }),
    ]);
  }, [queryClient, propertyTicker]);

  const onGetAll = useCallback(async () => {
    return queryClient.fetchQuery({
      queryKey: inventoryQueryKeys.table(propertyTicker),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      queryFn: async () => {
        const inventoryLinesRequest = new GetInventoryLinesRequest();
        inventoryLinesRequest.setOnlyValidLines(true);

        const [inventoryLinesResponse, currentInventoryStateList] = await Promise.all([
          getInventoryLines(inventoryLinesRequest),
          queryClient.fetchQuery({
            queryKey: inventoryQueryKeys.state(propertyTicker),
            queryFn: () => inventoryService.getCurrentInventoryState(propertyTicker),
            staleTime: 5 * 60 * 1000,
          }),
        ]);

        let responseObject = currentInventoryStateList.map(m => {
          return { ...m.toObject(), originalInventoryState: m } as ExtendedInventoryState;
        });
        const hasSameAccommodation = (a: InventoryState.AsObject, b: InventoryState.AsObject) =>
          a.accommodation === b.accommodation;
        const hasSameConfiguration = (a: InventoryState.AsObject, b: InventoryState.AsObject) =>
          a.configuration === b.configuration;
        const hasSameMealPlan = (a: InventoryState.AsObject, b: InventoryState.AsObject) =>
          a.mealPlan === b.mealPlan;
        responseObject = responseObject
          .sort((a, b) => a.accommodation.localeCompare(b.accommodation))
          .sort((a, b) => {
            if (hasSameAccommodation(a, b)) {
              return a.configuration.localeCompare(b.configuration);
            }
            return 0;
          })
          .sort((a, b) => {
            if (hasSameAccommodation(a, b) && hasSameConfiguration(a, b)) {
              return a.mealPlan.localeCompare(b.mealPlan);
            }
            return 0;
          })
          .sort((a, b) => {
            if (hasSameAccommodation(a, b) && hasSameConfiguration(a, b) && hasSameMealPlan(a, b)) {
              return a.condition.localeCompare(b.condition);
            }
            return 0;
          })
          .map((inv, idx) => {
            inv.id = idx;
            return inv;
          })
          .map(inv => {
            inv.checkinDaysList = daysInverter(inv.checkinDaysList);
            inv.checkoutDaysList = daysInverter(inv.checkoutDaysList);
            return inv;
          })
          .map(inv => {
            if (inv.closeToArrival) {
              inv.closeToArrivalOwn = inv.closeToArrival.own;
              inv.closeToArrival.days = inv.checkinDaysList;
            }
            if (inv.closeToDeparture) {
              inv.closeToDepartureOwn = inv.closeToDeparture.own;
              inv.closeToDeparture.days = inv.checkoutDaysList;
            }
            return inv;
          })
          .map(inv => {
            if (inv?.bookingWindowPeriod?.to && inv?.bookingWindowPeriod?.from) {
              //@ts-ignore
              const to = new proto.google.protobuf.Timestamp();
              to.setSeconds(inv.bookingWindowPeriod.to.seconds);
              to.setNanos(inv.bookingWindowPeriod.to.nanos);
              //@ts-ignore
              const from = new proto.google.protobuf.Timestamp();
              from.setSeconds(inv.bookingWindowPeriod.from.seconds);
              from.setNanos(inv.bookingWindowPeriod.from.nanos);
              inv.bookingWindowPeriod = {
                to: to.toDate().toISOString(),
                from: from.toDate().toISOString(),
              };
              inv.bookingWindowPeriodTo = dateToString(to.toDate());
              inv.bookingWindowPeriodFrom = dateToString(from.toDate());
            }
            return inv;
          })
          .map(it => {
            const inv = inventoryLinesResponse
              .getInventoryLinesList()
              .find(inv => inv.toObject().code === it.ticker);
            return {
              ...it,
              tickerName: inv
                ? `${inv
                    .getAccommodation()
                    ?.getName()} ${inv
                    .getConfiguration()
                    ?.getName()} ${inv.getMealPlan()?.getName()} ${inv.getCondition()?.getName()}`
                : "",
            };
          });
        return responseObject;
      },
    });
  }, [queryClient, getInventoryLines, inventoryService, propertyTicker]);

  const onGetOne = useCallback(
    async id => {
      const response = await onGetAll();

      return response.find(it => it.id === Number(id));
    },
    [onGetAll]
  );

  const onUpdate = useCallback(
    async (entity: ExtendedInventoryState) => {
      const inventoryState: InventoryState = entity.originalInventoryState;
      const request = new UpdateSingleInventoryLineRequest();
      request.setInventoryState(inventoryState);
      await updateSingleInventoryLine(request);
      await regenPropertyCache(httpClientCache, propertyTicker);
      await invalidateInventoryTableRows();
    },
    [updateSingleInventoryLine, httpClientCache, propertyTicker, invalidateInventoryTableRows]
  );

  const onDelete = useCallback(
    async (inventoryTicker: string) => {
      const request = new DeleteInventoryLineRequest();
      request.setInventoryTicker(inventoryTicker);
      await deleteInventoryLine(request);
      await regenPropertyCache(httpClientCache, propertyTicker);
      await invalidateInventoryTableRows();
    },
    [deleteInventoryLine, httpClientCache, propertyTicker, invalidateInventoryTableRows]
  );

  const onExport = useCallback(() => {
    window.location.href = `/v6/ng/${propertyTicker}/inventoryMultiUpdate`;
  }, [propertyTicker]);

  const handleVisibilityToggle = useCallback(
    async (row: ExtendedInventoryState) => {
      const inventoryState = row.originalInventoryState;
      inventoryState.setActive(!inventoryState.getActive());
      await onUpdate(row);
      setForceUpdate(forceUpdate + 1);
    },
    [forceUpdate, onUpdate]
  );

  const formConfiguration: FormConfiguration<ExtendedInventoryState> = useMemo(
    () => ({
      __fieldOrder: [
        "id",
        "active",
        "accommodation",
        "mealPlan",
        "configuration",
        "condition",
        "checkinDaysList",
        "checkoutDaysList",
        "ticker",
        "inventorySection",
        "rate",
        "availability",
        "lock",
        "rackRate",
        "validitySection",
        "bookingWindowPeriod",
        "restrictionsSection",
        "minStay",
        "maxStay",
        "minNotice",
        "maxNotice",
        "closeToArrival",
        "closeToDeparture",
        "*",
      ],
      id: {
        hidden: true,
      },
      active: {
        hidden: true,
      },
      accommodation: {
        hidden: true,
      },
      mealPlan: {
        hidden: true,
      },
      configuration: {
        hidden: true,
      },
      condition: {
        hidden: true,
      },
      checkinDaysList: {
        hidden: true,
      },
      checkoutDaysList: {
        hidden: true,
      },
      ticker: {
        readonly: true,
        messages: {
          label: defineMessage({
            id: "inventoryTable.CRU.ticker",
            defaultMessage: "ticker",
          }),
          helpText: defineMessage({
            id: "inventoryTable.CRU.tickerHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      inventorySection: {
        fieldWidget: SectionUnderline,
        messages: {
          label: defineMessage({
            id: "inventoryTable.inventorySection",
            defaultMessage: "inventorySection",
          }),
        },
      },
      availability: {
        fieldWidget: InventoryStateSelector,
        messages: {
          label: defineMessage({
            id: "inventoryTable.CRU.availability",
            defaultMessage: "availability",
          }),
          helpText: defineMessage({
            id: "inventoryTable.CRU.availabilityHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      lock: {
        fieldWidget: InventoryStateSelector,
        messages: {
          label: defineMessage({
            id: "inventoryTable.CRU.lock",
            defaultMessage: "lock",
          }),
          helpText: defineMessage({
            id: "inventoryTable.CRU.lockHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      rate: {
        fieldWidget: InventoryStateSelector,
        messages: {
          label: defineMessage({
            id: "inventoryTable.CRU.rate",
            defaultMessage: "rate",
          }),
          helpText: defineMessage({
            id: "inventoryTable.CRU.rateHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      rackRate: {
        fieldWidget: InventoryStateSelector,
        messages: {
          label: defineMessage({
            id: "inventoryTable.CRU.rackRate",
            defaultMessage: "rackRate",
          }),
          helpText: defineMessage({
            id: "inventoryTable.CRU.rackRateHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      validitySection: {
        fieldWidget: SectionUnderline,
        messages: {
          label: defineMessage({
            id: "inventoryTable.validitySection",
            defaultMessage: "validitySection",
          }),
        },
      },
      bookingWindowPeriod: {
        fieldWidget: DateRangePicker,
      },
      restrictionsSection: {
        fieldWidget: SectionUnderline,
        messages: {
          label: defineMessage({
            id: "inventoryTable.restrictionsSection",
            defaultMessage: "restrictionsSection",
          }),
        },
      },
      minStay: {
        fieldWidget: InventoryStateSelector,
        messages: {
          label: defineMessage({
            id: "inventoryTable.CRU.minStay",
            defaultMessage: "minStay",
          }),
          helpText: defineMessage({
            id: "inventoryTable.CRU.minStayHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      maxStay: {
        fieldWidget: InventoryStateSelector,
        messages: {
          label: defineMessage({
            id: "inventoryTable.CRU.maxStay",
            defaultMessage: "maxStay",
          }),
          helpText: defineMessage({
            id: "inventoryTable.CRU.maxStayHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      minNotice: {
        fieldWidget: InventoryStateSelector,
        messages: {
          label: defineMessage({
            id: "inventoryTable.CRU.minNotice",
            defaultMessage: "minNotice",
          }),
          helpText: defineMessage({
            id: "inventoryTable.CRU.minNoticeHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      maxNotice: {
        fieldWidget: InventoryStateSelector,
        messages: {
          label: defineMessage({
            id: "inventoryTable.CRU.maxNotice",
            defaultMessage: "maxNotice",
          }),
          helpText: defineMessage({
            id: "inventoryTable.CRU.maxNoticeHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      closeToArrival: {
        fieldWidget: InventoryStateSelector,
        messages: {
          label: defineMessage({
            id: "inventoryTable.CRU.CTA",
            defaultMessage: "CTA",
          }),
          helpText: defineMessage({
            id: "inventoryTable.CRU.CTAHelpText",
            defaultMessage: "CTA",
          }),
        },
      },
      closeToDeparture: {
        fieldWidget: InventoryStateSelector,
        messages: {
          label: defineMessage({
            id: "inventoryTable.CRU.CTD",
            defaultMessage: "CTD",
          }),
          helpText: defineMessage({
            id: "inventoryTable.CRU.CTDHelpText",
            defaultMessage: "CTD",
          }),
        },
      },
      codesList: {
        fieldWidget: ArrayAsString,
        messages: {
          label: defineMessage({
            id: "inventoryTable.CRU.codes",
            defaultMessage: "codes",
          }),
          helpText: defineMessage({
            id: "inventoryTable.CRU.codesHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
    }),
    []
  );

  const InventoryStateObjectRenderer = useCallback(
    ({
      value,
    }: RendererProps<
      { [_: string]: string | number | { value: string | number } } | undefined
    >) => {
      let val = Object.values(value || {}).find(it => it);
      let finalValue: string | number = "";
      if (val === "Propia") {
        finalValue = intl.formatMessage(msgs.own);
      } else if (typeof val === "object" && val.value !== undefined && val.value !== null) {
        finalValue = val.value;
      } else if (val && typeof val === "string") {
        finalValue = val;
      }

      return <div>{finalValue}</div>;
    },
    [intl]
  );

  const InventoryStateObjectRateRenderer = useCallback(
    ({ value }: RendererProps<RateValue.AsObject | undefined>) => {
      if (!value) return <div />;

      const rateValue = Object.values(value).find(Boolean);

      if (rateValue === "Propia") {
        return <div>{intl.formatMessage(msgs.own)}</div>;
      }

      if (typeof rateValue === "string") {
        return <div>{rateValue}</div>;
      }

      return <div>{rateValue?.rateOwner ?? ""}</div>;
    },
    [intl]
  );

  const InventoryStateDaysRenderer = useCallback(
    ({ value }: RendererProps<DayOfWeek[]>) => {
      const val = value && value.map(day => intl.formatMessage(msgs[day])).join(",");
      return <div>{val}</div>;
    },
    [intl]
  );

  const InventoryFullTickerRenderer = useCallback(
    ({ rows }: RendererProps<string>) => (
      <OverlayTrigger {...tooltipConfig} overlay={props => fullInventoryTickerTooltip(props, rows)}>
        <div>{rows?.ticker}</div>
      </OverlayTrigger>
    ),
    []
  );

  const formattedColumns: TableColumns<ExtendedInventoryState> = {
    accommodation: {
      renderer: TableRenderers.StringRenderer,
      width: 100,
    },
    configuration: {
      renderer: TableRenderers.StringRenderer,
      width: 70,
    },
    ticker: {
      renderer: InventoryFullTickerRenderer,
      width: 170,
    },
    mealPlan: {
      renderer: TableRenderers.StringRenderer,
      width: 100,
    },
    condition: {
      renderer: TableRenderers.StringRenderer,
      width: 100,
    },
    rate: {
      renderer: InventoryStateObjectRateRenderer,
      width: 180,
    },
    availability: {
      renderer: InventoryStateObjectRenderer,
      width: 150,
    },
    lock: {
      renderer: InventoryStateObjectRenderer,
      width: 150,
    },
    minStay: {
      renderer: InventoryStateObjectRenderer,
      width: 150,
    },
    maxStay: {
      renderer: InventoryStateObjectRenderer,
      width: 150,
    },
    minNotice: {
      renderer: InventoryStateObjectRenderer,
      width: 150,
    },
    maxNotice: {
      renderer: InventoryStateObjectRenderer,
      width: 150,
    },
    codesList: {
      renderer: mapRenderer(StringRenderer, v => {
        return v?.toString();
      }),
      width: 100,
    },
    checkinDaysList: {
      renderer: InventoryStateDaysRenderer,
      width: 100,
    },
    checkoutDaysList: {
      renderer: InventoryStateDaysRenderer,
      width: 100,
    },
    rackRate: {
      renderer: InventoryStateObjectRenderer,
      width: 60,
    },
    bookingWindowPeriodFrom: {
      renderer: TableRenderers.StringRenderer,
      width: 100,
    },
    bookingWindowPeriodTo: {
      renderer: TableRenderers.StringRenderer,
      width: 100,
    },
    closeToArrivalOwn: {
      renderer: TableRenderers.StringRenderer,
      width: 80,
    },
    closeToDepartureOwn: {
      renderer: TableRenderers.StringRenderer,
      width: 80,
    },
  };

  const tableFilters: TableFilters<ExtendedInventoryState, ExtendedInventoryState> = useMemo(() => {
    return {
      ticker: {
        filterFn: syncFilter,
        widget: inputFilter,
      },
      accommodation: {
        filterFn: syncFilter,
        widget: inputFilter,
      },
      configuration: {
        filterFn: syncFilter,
        widget: inputFilter,
      },
      mealPlan: {
        filterFn: syncFilter,
        widget: inputFilter,
      },
      condition: {
        filterFn: syncFilter,
        widget: inputFilter,
      },
    };
  }, []);

  const TableRowActions = useCallback(
    ({ row, rest }: DefaultTableRowActionsProps<ExtendedInventoryState>) => {
      return (
        <Table.Cell {...rest}>
          {/*Visibility*/}
          <OverlayTrigger {...tooltipConfig} overlay={visibilityTooltip}>
            <Button
              {...buttonConfig}
              onClick={() => {
                handleVisibilityToggle(row);
              }}>
              {row.active ? (
                <FontAwesomeIcon icon={faEye} />
              ) : (
                <StyledFontAwesomeIcon icon={faEyeSlash} />
              )}
              {/*faEyeSlash*/}
            </Button>
          </OverlayTrigger>

          <OverlayTrigger {...tooltipConfig} overlay={deleteTooltip}>
            <span>
              <Delete
                rest={rest}
                onDelete={() => onDelete(row.ticker)}
                row={row}
                onReloadList={() => setForceUpdate(forceUpdate + 1)}
              />
            </span>
          </OverlayTrigger>
        </Table.Cell>
      );
    },
    [handleVisibilityToggle, onDelete, forceUpdate]
  );

  return (
    <SimpleCrud<ExtendedInventoryState, ExtendedInventoryState>
      formContainer={FormContainer}
      section={section}
      schema={modifiedSchema}
      formConfiguration={formConfiguration}
      messages={messages}
      tableColumns={formattedColumns}
      rowActions={props => <TableRowActions {...props} />}
      onGetAll={onGetAll}
      onExport={onExport}
      onExportText={intl.formatMessage(msgs.onExportText)}
      uncontrolledPaging={true}
      columnVisibility={true}
      pageSizes={pageSizes}
      defaultPageSize={defaultPageSize}
      tableFilters={tableFilters}
      managerName={"inventoryTable"}
      fixedActions={true}
      //remove if getSingleInventory is added
      //@ts-ignore
      onGetOne={onGetOne}
      onUpdate={onUpdate}
      forceLoad={forceUpdate}
    />
  );
}

export default withAuthorizationVerifier(InventoryTable);
